import React, { useEffect, useState } from 'react'
import {
  RouteComponentProps,
  Link as RouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks'
import {
  Branch,
  PAYMENT_ACCOUNT_LABELS,
  PaymentAccount,
  Roles,
  User,
  VoucherApproval,
  VoucherApprovalStageStatus,
  VoucherCostCategory,
  VoucherState,
  VoucherType,
} from '../../types'
import { useAuthentication } from '../../hooks/useAuthentication'
import { DRAWER_WIDTH } from '../../layout/Sidebar'

import { Link as MuiLink, Container } from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Grid2 as Grid,
  Box,
  Button,
  IconButton,
  TextField,
  Card,
  CardContent,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Checkbox,
  Alert,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import sv from 'date-fns/locale/sv'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import VoucherTags from '../Tags/VoucherTags'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import MailIcon from '@mui/icons-material/Mail'
import AssessmentIcon from '@mui/icons-material/Assessment'

import CustomDialog from '../CustomDialog'
import CategoryHelpList from '../Vouchers/CategoryHelpList'
import CostCategories from '../Vouchers/CostCategories'
import Loader from '../Loader'
import VoucherStateBadge from '../table/VoucherStateBadge'
import {
  APPROVE_VOUCHER,
  PAID_VOUCHER,
  REJECT_VOUCHER,
  GET_VOUCHER_BUDGET_IMPACT,
} from './gql'
import {
  GET_VOUCHER_BY_ID,
  UPDATE_VOUCHER,
  GET_CATEGORIES,
  VoucherUpdateInputMap,
  CREATE_VOUCHER_COST_CATEGORY,
  UPDATE_VOUCHER_COST_CATEGORY,
  DELETE_VOUCHER_COST_CATEGORY,
  GET_DEPARTMENTS,
  GET_USERS_LIST,
  GET_DOWNLOAD_LINK,
  GET_FINANCIAL_YEARS,
} from '../Vouchers/gql'
import { buildVoucherPdf, downloadPdfBytes } from '../../utils/voucherPdfExport'
import VoucherComments from '../Vouchers/VoucherComments'
import ApprovalStages from '../Common/ApprovalStages'
import VhsDialog from '../Common/VhsDialog'
import { toast } from 'react-toastify'
import {
  getCurrentExpensePeriod,
  getExpenseMonthOptions,
  getCurrentApprover,
  isRoleAllowedPayments,
  getAvailableDepartments,
  getCanChangeDepartment,
  willVoucherReachApproved,
} from '../../utils/utils'
import { GET_NOTIFICATION_COUNT } from '../../layout/Sidebar'
import { GET_INBOX } from './gql'

type VoucherProps = RouteComponentProps<{ id?: string }>

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '0.72rem',
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
)

const selectSx = {
  fontSize: '0.95rem',
  '& .MuiSelect-select': { py: 1.2 },
}

const formatSek = (amount: number) =>
  new Intl.NumberFormat('en-SE', {
    style: 'currency',
    currency: 'SEK',
  }).format(amount)

const getEditableSnapshot = (
  voucher: VoucherType,
  departmentId: string,
  financialYearId: number | null,
) =>
  JSON.stringify({
    description: voucher.description || '',
    department: departmentId,
    financial_year: financialYearId,
    pay_to: voucher.pay_to || '',
    expense_period: voucher.expense_period ?? null,
    payment_account: voucher.payment_account ?? null,
    currency: voucher.currency || '',
    invoice_currency: voucher.invoice_currency || 'SEK',
  })

const Details: React.FunctionComponent<VoucherProps> = ({ match }) => {
  const { authState } = useAuthentication()
  const location = useLocation()
  const isFromVouchers = location.pathname.includes('/panel/vouchers')
  const isFromReports = location.pathname.includes('/panel/reports')
  const backUrl = isFromReports
    ? '/panel/reports'
    : isFromVouchers
      ? '/panel/vouchers'
      : '/panel/inbox'
  const originLabel = isFromReports
    ? 'Reports'
    : isFromVouchers
      ? 'Vouchers'
      : 'Inbox'
  const OriginIcon = isFromReports
    ? AssessmentIcon
    : isFromVouchers
      ? DescriptionOutlinedIcon
      : MailIcon

  const apolloClient = useApolloClient()
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  const { loading, error, data } = useQuery(GET_VOUCHER_BY_ID, {
    variables: { id: match.params.id },
  })
  const departmentsResult = useQuery(GET_DEPARTMENTS)
  const financialYearsResult = useQuery(GET_FINANCIAL_YEARS)
  const usersResult = useQuery(GET_USERS_LIST)

  const categoriesResult = useQuery(GET_CATEGORIES)
  const [updateVoucher] = useMutation(UPDATE_VOUCHER)
  const [createVoucherCostCategory] = useMutation(CREATE_VOUCHER_COST_CATEGORY)
  const [updateVoucherCostCategory] = useMutation(UPDATE_VOUCHER_COST_CATEGORY)
  const [deleteVoucherCostCategory] = useMutation(DELETE_VOUCHER_COST_CATEGORY)

  const [approveVoucher] = useMutation(APPROVE_VOUCHER)
  const [paidVoucher] = useMutation(PAID_VOUCHER)
  const [rejectVoucher, rejectVoucherResp] = useMutation(REJECT_VOUCHER)
  const history = useHistory()
  const today = new Date()
  const [voucher, setVoucher] = useState<VoucherType>({
    id: match.params.id || '',
    reference_number: null,
    expense_period: null,
    description: '',
    state: VoucherState.DRAFT,
    total_amount: 0,
    voucher_cost_category: [] as VoucherCostCategory[],
    voucher_approvals: [] as VoucherApproval[],
    created_by: { id: '', first_name: '', last_name: '', email: '' },
    pay_to: '',
    claimed_by: { id: '', first_name: '', last_name: '', email: '' },
    department: { id: '', name: '', national_only: false },
    paid_by_amin: null,
    payment_reference: '',
    receivers_account_number: '',
    created_at: new Date().getTime(),
    submitted_at: null,
    financial_year: { id: 0, year: '', start_date: '', end_date: '' },
    payment_account: 'AMIN',
    currency: 'SEK',
    invoice_currency: 'SEK',
    workflow: null,
    branch: '',
  })

  const isNationalMaal = authState.authUser.role_id === Roles.NATIONAL.MAAL.id

  const isAmin =
    authState.authUser.role_id === Roles.NATIONAL.ADMIN.id ||
    authState.authUser.role_id === Roles.BRANCH.AMIN.id

  const isApprover = voucher.voucher_approvals.some(
    (approval) => approval.approver?.id === authState.authUser.id,
  )

  const isOwner = voucher.created_by?.id === authState.authUser.id
  const isOwnerByDepartment =
    voucher.department?.id === authState.authUser.department_id &&
    voucher.branch === authState.authUser.branch

  const allowedToEdit =
    isNationalMaal ||
    ((isOwner || isOwnerByDepartment || isApprover) &&
      voucher.state !== VoucherState.APPROVED &&
      voucher.state !== VoucherState.PAID)

  const isAwaitingApproval =
    voucher.state === VoucherState.SUBMITTED ||
    voucher.state === VoucherState.IN_PROGRESS

  // Shown as an always-visible panel to any viewer (not just the approver about
  // to act), and re-fetched right before the final-approval warning check so
  // that check reflects the latest commitments from other vouchers/approvers.
  const voucherBudgetImpactResult = useQuery(GET_VOUCHER_BUDGET_IMPACT, {
    variables: { voucher_id: match.params.id },
    skip: !match.params.id || !isAwaitingApproval,
    fetchPolicy: 'cache-and-network',
  })

  const isCurrentApprover =
    getCurrentApprover(voucher)?.id === authState.authUser.id

  const isSubmittedAndNotYetApproved =
    (voucher.state === VoucherState.SUBMITTED ||
      voucher.state === VoucherState.IN_PROGRESS) &&
    voucher.submitted_at !== null

  const isForeignInvoice = (voucher.invoice_currency || 'SEK') !== 'SEK'
  const isForeignAmount = !!voucher.currency && voucher.currency !== 'SEK'
  const isPaymentStage = voucher.state === VoucherState.APPROVED
  const showAmountCurrencyOptions =
    isForeignInvoice && (voucher.state !== VoucherState.PAID || isForeignAmount)

  const canEditCurrency =
    allowedToEdit &&
    (isNationalMaal ||
      (voucher.created_by?.user_role?.id !== Roles.NATIONAL.MAAL.id &&
        !voucher.voucher_approvals.some(
          (approval) =>
            approval.status === 'APPROVED' &&
            approval.approver?.user_role?.id === Roles.NATIONAL.MAAL.id,
        )))

  const handleInvoiceCurrencyChange = (invoice_currency: string) =>
    setVoucher((prev) => {
      if (prev.state === VoucherState.PAID && prev.currency === 'SEK') {
        return { ...prev, invoice_currency }
      }
      if (invoice_currency === 'SEK') {
        return {
          ...prev,
          invoice_currency,
          currency: 'SEK',
          payment_account: prev.payment_account ?? 'AMIN',
        }
      }
      const wasForeignInvoice = (prev.invoice_currency || 'SEK') !== 'SEK'
      const currency =
        wasForeignInvoice && prev.currency === 'SEK'
          ? 'SEK'
          : wasForeignInvoice
            ? invoice_currency
            : ''
      return { ...prev, invoice_currency, currency }
    })

  const handleAmountCurrencyChange = (currency: string) =>
    setVoucher((prev) => {
      let payment_account = prev.payment_account
      if (currency === 'SEK') {
        payment_account = payment_account ?? 'AMIN'
      } else if (payment_account === 'AMIN') {
        // Amin's account can't pay this. The server applies the same default.
        const isMaalDepartment =
          departments.find((d) => d.id === selectedDepartment)?.name === 'Maal'
        payment_account = isMaalDepartment ? 'HUVUDKONTO' : null
      }
      return { ...prev, currency, payment_account }
    })

  const isApprovalDisabled =
    isSubmittedAndNotYetApproved &&
    (voucher.total_amount === 0 || voucher.voucher_cost_category.length === 0)

  const isMarkingAsPaid =
    isRoleAllowedPayments(authState.authUser.role_id) &&
    voucher.state === VoucherState.APPROVED

  const isPaymentBlockedByCurrency = isMarkingAsPaid && isForeignAmount

  const hasAnyReceipts = voucher.voucher_cost_category.some(
    (vcc) => vcc.receipts?.length,
  )

  const canChangeDepartment = getCanChangeDepartment(
    voucher,
    authState.authUser.role_id,
    authState.authUser.email,
    authState.authUser.id,
  )

  const [categories, setCategories] = useState<Array<any>>([])

  const [departments, setDepartments] = React.useState<Array<any>>([])
  const [financialYears, setFinancialYears] = React.useState<Array<any>>([])
  const [users, setUsers] = React.useState<Array<any>>([])
  const [selectedDepartment, setSelectedDepartment] = React.useState('')
  const [selectedFinancialYear, setSelectedFinancialYear] = React.useState<
    number | null
  >(null)
  const [comment, setComment] = React.useState('')
  const [payToType, setPayToType] = useState<'AMLA' | 'NON_AMLA' | ''>('')
  const [helpOpen, setHelpOpen] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [submissionConfirmOpen, setSubmissionConfirmOpen] = useState(false)
  const [initialDescription, setInitialDescription] = useState('')
  const [initialVoucherSnapshot, setInitialVoucherSnapshot] = useState('')
  const [isDescriptionEditing, setIsDescriptionEditing] = useState(false)
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false)
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false)
  const [budgetWarning, setBudgetWarning] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<'BACK' | 'APPROVE' | null>(
    null,
  )

  useEffect(() => {
    if (voucher.pay_to && users.length > 0 && !payToType) {
      const isAmla = users.some(
        (u) => `${u.first_name} ${u.last_name}` === voucher.pay_to,
      )
      setPayToType(isAmla ? 'AMLA' : 'NON_AMLA')
    }
  }, [voucher.pay_to, users, payToType])

  const handleDownloadVoucherPdf = async () => {
    setIsDownloadingPdf(true)
    try {
      const attachments = (voucher.voucher_cost_category || []).flatMap(
        (category: any) =>
          (category.receipts || []).map((receipt: any) => ({
            id: receipt.id,
            path: receipt.path,
          })),
      )

      const bytes = await buildVoucherPdf(voucher, attachments, async (id) => {
        const { data: linkData } = await apolloClient.query({
          query: GET_DOWNLOAD_LINK,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        return linkData.signedDownloadLink.signedLink
      })

      downloadPdfBytes(
        bytes,
        `voucher-${voucher.reference_number || voucher.id}.pdf`,
      )
    } catch (err: any) {
      console.error('Failed to build voucher PDF', err)
      toast.error(err.message || 'Could not generate the voucher PDF')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  useEffect(() => {
    if (loading === false && data) {
      const fetchedVoucher = { ...data.voucher }

      if (
        isRoleAllowedPayments(authState.authUser.role_id) &&
        fetchedVoucher.state === VoucherState.APPROVED
      ) {
        if (!fetchedVoucher.paid_by_amin) {
          fetchedVoucher.paid_by_amin = new Date().toISOString()
        }
        if (!fetchedVoucher.expense_period) {
          fetchedVoucher.expense_period = getCurrentExpensePeriod()
        }
      }

      setVoucher(fetchedVoucher)
      setInitialDescription(fetchedVoucher.description || '')
      setIsDescriptionEditing(!fetchedVoucher.description)
      setComment(fetchedVoucher.comment)
      setSelectedDepartment(fetchedVoucher.department.id)
      setSelectedFinancialYear(fetchedVoucher.financial_year.id)
      setInitialVoucherSnapshot(
        getEditableSnapshot(
          fetchedVoucher,
          fetchedVoucher.department.id,
          fetchedVoucher.financial_year.id,
        ),
      )
    }
  }, [loading, data, authState.authUser.id, authState.authUser.role_id])

  useEffect(() => {
    if (categoriesResult.loading === false && categoriesResult.data) {
      setCategories(categoriesResult.data.costCategories)
    }
  }, [categoriesResult.loading, categoriesResult.data])

  useEffect(() => {
    if (departmentsResult.loading === false && departmentsResult.data) {
      setDepartments(departmentsResult.data.departments)
    }
  }, [departmentsResult.loading, departmentsResult.data])

  useEffect(() => {
    if (financialYearsResult.loading === false && financialYearsResult.data) {
      setFinancialYears(financialYearsResult.data.financialYears)
    }
  }, [financialYearsResult.loading, financialYearsResult.data])

  useEffect(() => {
    if (usersResult.loading === false && usersResult.data) {
      setUsers(usersResult.data.users)
    }
  }, [usersResult.loading, usersResult.data])

  if (loading) return <Loader />
  if (error) return <p>ERROR: {error?.message}</p>

  // Cost Category Callback Functions
  const handleAddCostCategory = async (costCategory: any) => {
    try {
      const response = await createVoucherCostCategory({
        variables: {
          input: {
            voucher_id: voucher.id,
            sub_cost_category_id: costCategory.sub_cost_category.id,
            cost_share: costCategory.cost_share,
            comment: costCategory.comment,
          },
        },
        context: { headers: { authorization: authState.authToken } },
      })

      if (response.data?.createVoucherCostCategory) {
        const createdCat = response.data.createVoucherCostCategory
        const newCostCategory = {
          id: createdCat.id,
          sub_cost_category: costCategory.sub_cost_category,
          cost_share: costCategory.cost_share,
          comment: costCategory.comment,
          receipts: [],
        }

        setVoucher((prev) => ({
          ...prev,
          total_amount: createdCat.voucher?.total_amount ?? prev.total_amount,
          voucher_cost_category: [
            ...prev.voucher_cost_category,
            newCostCategory,
          ],
        }))

        if (isAwaitingApproval) {
          voucherBudgetImpactResult.refetch()
        }

        return newCostCategory
      }
    } catch (error) {
      console.error('Error adding cost category:', error)
      throw error
    }
  }

  const handleUpdateCostCategory = async (id: string, updates: any) => {
    try {
      if (updates.cost_category || updates.cost_share !== undefined) {
        const response = await updateVoucherCostCategory({
          variables: {
            input: {
              id: id,
              sub_cost_category_id: updates.sub_cost_category?.id,
              cost_share: updates.cost_share,
              comment: updates.comment,
            },
          },
          context: { headers: { authorization: authState.authToken } },
        })

        if (response.data?.updateVoucherCostCategory) {
          const updatedCat = response.data.updateVoucherCostCategory
          setVoucher((prev) => ({
            ...prev,
            total_amount: updatedCat.voucher?.total_amount ?? prev.total_amount,
            voucher_cost_category: prev.voucher_cost_category.map((cat) =>
              cat.id?.toString() === id
                ? {
                    ...cat,
                    sub_cost_category:
                      updates.sub_cost_category || cat.sub_cost_category,
                    cost_share:
                      updates.cost_share !== undefined
                        ? updates.cost_share
                        : cat.cost_share,
                    comment:
                      updates.comment !== undefined
                        ? updates.comment
                        : cat.comment,
                  }
                : cat,
            ),
          }))

          if (isAwaitingApproval) {
            voucherBudgetImpactResult.refetch()
          }
        }
      } else {
        setVoucher((prev) => ({
          ...prev,
          voucher_cost_category: prev.voucher_cost_category.map((cat) =>
            cat.id?.toString() === id ? { ...cat, ...updates } : cat,
          ),
        }))
      }
    } catch (error) {
      console.error('Error updating cost category:', error)
      throw error
    }
  }

  const handleDeleteCostCategory = async (id: string) => {
    try {
      const response = await deleteVoucherCostCategory({
        variables: { id },
        context: { headers: { authorization: authState.authToken } },
      })

      if (response.data?.deleteVoucherCostCategory) {
        const deletedCat = response.data.deleteVoucherCostCategory
        setVoucher((prev) => ({
          ...prev,
          total_amount: deletedCat.voucher?.total_amount ?? 0,
          voucher_cost_category: prev.voucher_cost_category.filter(
            (cat) => cat.id?.toString() !== id,
          ),
        }))

        if (isAwaitingApproval) {
          voucherBudgetImpactResult.refetch()
        }
      }
    } catch (error) {
      console.error('Error deleting cost category:', error)
      throw error
    }
  }

  const getValidationError = () => {
    if (isForeignInvoice && !voucher.currency) {
      return 'Choose whether you already paid this or the supplier should be paid.'
    }
    if (!voucher.description || voucher.description.trim() === '') {
      return 'Description is required.'
    }
    if (!voucher.pay_to || voucher.pay_to.trim() === '') {
      return 'Pay To is required.'
    }
    if (!voucher.voucher_cost_category.length) {
      return 'You must add at least one cost category.'
    }
    const subCategoryIds = voucher.voucher_cost_category
      .map((vcc) => vcc.sub_cost_category?.id)
      .filter(Boolean)
    if (new Set(subCategoryIds).size !== subCategoryIds.length) {
      return 'Each cost category (Parent and Sub-category) can only be used once.'
    }
    if (
      voucher.voucher_cost_category.some(
        (c) =>
          (typeof c.cost_share === 'string'
            ? parseFloat(c.cost_share) || 0
            : c.cost_share || 0) <= 0,
      )
    ) {
      return 'The amount for each cost category must be greater than 0.'
    }
    return ''
  }

  const formatDate = (dateValue: any) => {
    if (!dateValue) return ''
    try {
      const date = new Date(isNaN(dateValue) ? dateValue : dateValue * 1)
      if (isNaN(date.getTime())) return ''
      return date.toISOString().split('T')[0]
    } catch (e) {
      return ''
    }
  }

  const validationError = getValidationError()

  const descriptionHasContent = !!voucher.description?.trim()
  const showDescriptionRequiredError =
    !descriptionHasContent &&
    (showValidationError || voucher.state !== VoucherState.DRAFT)
  const showDescriptionEditor =
    allowedToEdit && (isDescriptionEditing || !descriptionHasContent)

  const isVoucherDirty =
    getEditableSnapshot(voucher, selectedDepartment, selectedFinancialYear) !==
    initialVoucherSnapshot

  const hasBottomActions =
    (voucher.state === VoucherState.DRAFT && !!voucher.workflow) ||
    ((allowedToEdit || canChangeDepartment) &&
      voucher.state !== VoucherState.DRAFT &&
      voucher.state !== VoucherState.REJECTED &&
      isVoucherDirty)
  const showBottomBar =
    hasBottomActions || (showValidationError && !!validationError)

  const handleSubmit = async (
    state: VoucherState.DRAFT | VoucherState.SUBMITTED,
  ) => {
    if (
      state === VoucherState.SUBMITTED &&
      voucher.state === VoucherState.DRAFT &&
      validationError
    ) {
      setShowValidationError(true)
      return
    }
    if (isForeignInvoice && !voucher.currency) {
      setShowValidationError(true)
      return
    }
    setShowValidationError(false)

    try {
      const voucherInput = VoucherUpdateInputMap(voucher)
      voucherInput.id = voucher.id
      voucherInput.state = state
      voucherInput.submitted_at =
        state === VoucherState.SUBMITTED ? new Date().toISOString() : null
      if (selectedDepartment) {
        voucherInput.department = { connect: { id: selectedDepartment } }
      }

      if (selectedFinancialYear) {
        voucherInput.financial_year = {
          connect: { id: selectedFinancialYear.toString() },
        }
      }

      if (voucher.pay_to) {
        voucherInput.pay_to = voucher.pay_to
      }

      const { data } = await updateVoucher({
        variables: { input: voucherInput },
      })

      if (data) {
        const referenceNumber =
          data.updateVoucher?.reference_number ??
          voucher.reference_number ??
          'Draft'
        toast.success(
          `Voucher ${referenceNumber} ${
            state === VoucherState.SUBMITTED ? 'submitted' : 'updated'
          } successfully`,
        )
        if (state === VoucherState.SUBMITTED) {
          history.push(backUrl)
        }
      }
    } catch (err: any) {
      console.error(err)
      toast.error(
        err.message ||
          `Voucher ${voucher.reference_number ?? 'Draft'} updated failed`,
      )
    }
  }

  const handleUpdate = async () => {
    try {
      if (
        (voucher.state !== VoucherState.DRAFT && validationError) ||
        (isForeignInvoice && !voucher.currency)
      ) {
        setShowValidationError(true)
        return
      }
      setShowValidationError(false)
      const voucherInput = VoucherUpdateInputMap(voucher)
      if (selectedDepartment) {
        voucherInput.department = { connect: { id: selectedDepartment } }
      }

      if (selectedFinancialYear) {
        voucherInput.financial_year = {
          connect: { id: selectedFinancialYear.toString() },
        }
      }

      const { data } = await updateVoucher({
        variables: { input: voucherInput },
      })

      if (data) {
        if (isAwaitingApproval) {
          voucherBudgetImpactResult.refetch()
        }
        const referenceNumber =
          data.updateVoucher?.reference_number ??
          voucher.reference_number ??
          'Draft'
        toast.success(`Voucher ${referenceNumber} updated successfully`)
        setInitialDescription(voucher.description || '')
        setInitialVoucherSnapshot(
          getEditableSnapshot(
            voucher,
            selectedDepartment,
            selectedFinancialYear,
          ),
        )
        return data
      }
    } catch (err: any) {
      console.error('Error updating voucher:', err.message)
      toast.error(
        err.message ||
          `Voucher ${voucher.reference_number ?? 'Draft'} updated failed`,
      )
    }
    return null
  }

  const handleApprove = async () => {
    try {
      if (isMarkingAsPaid) {
        const paidVoucherInput = {
          voucher_id: voucher.id,
          paid_by_amin: voucher.paid_by_amin,
          payment_reference: voucher.payment_reference,
          expense_period: voucher.expense_period,
        }
        const { data } = await paidVoucher({
          variables: { input: paidVoucherInput },
          refetchQueries: [
            { query: GET_NOTIFICATION_COUNT },
            { query: GET_INBOX },
          ],
          awaitRefetchQueries: true,
        })
        if (data) {
          toast.success(`Voucher ${voucher.reference_number} marked as paid`)
          history.push(backUrl)
        }
      } else {
        const approveVoucherInput = {
          voucherId: voucher.id,
          status: VoucherApprovalStageStatus.APPROVED,
          comment: comment || null,
        }
        const { data } = await approveVoucher({
          variables: { input: approveVoucherInput },
          refetchQueries: [
            { query: GET_NOTIFICATION_COUNT },
            { query: GET_INBOX },
          ],
          awaitRefetchQueries: true,
        })
        if (data) {
          toast.success(
            `Voucher ${voucher.reference_number} approved successfully`,
          )
          history.push(backUrl)
        }
      }
    } catch (err: any) {
      console.error(err)
      toast.error(
        err.message || `Voucher ${voucher.reference_number} update failed`,
      )
    }
  }

  const openApproveConfirm = async () => {
    setBudgetWarning(null)
    if (willVoucherReachApproved(voucher)) {
      try {
        // Refetch rather than reuse the panel's data -- other vouchers may
        // have been approved since this page loaded, and this check should
        // reflect the latest commitments at the moment of the decision.
        const { data } = await voucherBudgetImpactResult.refetch()
        const exceeded = (data?.voucherBudgetImpact ?? []).filter(
          (line: any) => line.exceeds_budget,
        )
        if (exceeded.length > 0) {
          const details = exceeded
            .map(
              (line: any) =>
                `${line.sub_cost_category?.name ?? line.cost_category.name} (over by ${formatSek(line.amount_over)})`,
            )
            .join(', ')
          setBudgetWarning(
            `Warning: approving this voucher will exceed the department's budget for: ${details}.`,
          )
        }
      } catch (err) {
        console.error('Could not check budget impact:', err)
      }
    }
    setApproveConfirmOpen(true)
  }

  const proceedWithAction = (action: 'BACK' | 'APPROVE') => {
    if (action === 'BACK') {
      history.push(backUrl)
    } else if (action === 'APPROVE') {
      openApproveConfirm()
    }
  }

  const handleConfirmUnsaved = async () => {
    const data = await handleUpdate()
    if (data) {
      const action = pendingAction
      setUnsavedChangesDialogOpen(false)
      setPendingAction(null)
      if (action) proceedWithAction(action)
    }
  }

  const handleDiscardUnsaved = () => {
    const action = pendingAction
    setUnsavedChangesDialogOpen(false)
    setPendingAction(null)
    if (action) proceedWithAction(action)
  }

  const handleConfirmReject = async (reason: string) => {
    try {
      const { data } = await rejectVoucher({
        variables: {
          input: { voucherId: voucher.id, comment: reason },
        },
        refetchQueries: [
          { query: GET_NOTIFICATION_COUNT },
          { query: GET_INBOX },
        ],
        awaitRefetchQueries: true,
      })
      if (data) {
        setRejectDialogOpen(false)
        toast.success(
          `Voucher ${voucher.reference_number} rejected successfully`,
        )
        history.push(backUrl)
      }
    } catch (err: any) {
      console.error(err)
      toast.error(
        err.message || `Voucher ${voucher.reference_number} rejection failed`,
      )
    }
  }

  const rejectionRoles = [
    'LOCAL_SEC_MALL',
    'LOCAL_PRESIDENT',
    'NATIONAL_SEC_MALL',
  ]

  // if (rejectVoucherResp.error) {
  //   console.error('Error Paid Voucher: ', rejectVoucherResp.error)
  // }

  // if (rejectVoucherResp.loading === false && rejectVoucherResp.data) {
  //   return <Redirect to="/panel/inbox" />
  // }

  return (
    <Paper square>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          flexWrap: 'wrap',
        }}
      >
        <IconButton
          onClick={() => {
            if (voucher.description !== initialDescription) {
              setPendingAction('BACK')
              setUnsavedChangesDialogOpen(true)
            } else {
              history.push(backUrl)
            }
          }}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            flexShrink: 0,
          }}
        >
          <OriginIcon fontSize="small" />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontWeight: 700,
              fontSize: '1.2rem',
              lineHeight: 1.25,
              fontFamily: 'monospace',
            }}
          >
            {voucher.reference_number
              ? `Voucher #${voucher.reference_number}`
              : 'Voucher (Draft)'}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            From {originLabel}
            {voucher.department?.name ? ` · ${voucher.department.name}` : ''}
            {voucher.created_by?.first_name
              ? ` · ${voucher.created_by.first_name} ${voucher.created_by.last_name}`
              : ''}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={isDownloadingPdf}
            onClick={handleDownloadVoucherPdf}
            startIcon={
              isDownloadingPdf ? (
                <CircularProgress size={16} />
              ) : (
                <PictureAsPdfIcon fontSize="small" />
              )
            }
          >
            {isDownloadingPdf ? 'Preparing...' : 'Download PDF'}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            lineHeight: 1.25,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            Amount
          </Typography>
          <Typography
            sx={{
              fontSize: '1.15rem',
              fontWeight: 700,
              color: 'success.main',
              fontFamily: 'monospace',
            }}
          >
            {voucher.total_amount} {voucher.currency}
          </Typography>
        </Box>

        <VoucherStateBadge state={voucher.state} />
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          mt: 2,
          mb: 2,
          px: { xs: 1, sm: 2 },
          pb: showBottomBar
            ? showValidationError && validationError
              ? 16
              : 10
            : 0,
        }}
      >
        <Grid container spacing={1.5}>
          {/* Main Content Column (75%) */}
          <Grid size={{ xs: 12, md: 9 }} order={{ xs: 2, md: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <DescriptionOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'primary.main' }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, flexGrow: 1 }}
                  >
                    Expense Description
                  </Typography>
                  {allowedToEdit && !showDescriptionEditor && (
                    <IconButton
                      size="small"
                      onClick={() => setIsDescriptionEditing(true)}
                      aria-label="Edit expense description"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {allowedToEdit &&
                    showDescriptionEditor &&
                    descriptionHasContent && (
                      <IconButton
                        size="small"
                        onClick={() => setIsDescriptionEditing(false)}
                        aria-label="Done editing expense description"
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    )}
                </Box>

                {showDescriptionEditor ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="What was this expense for? Include the event, purpose, and who it benefits."
                    value={voucher.description || ''}
                    onChange={(e) =>
                      setVoucher({ ...voucher, description: e.target.value })
                    }
                    size="small"
                    InputProps={{ style: { fontSize: '0.925rem' } }}
                    error={showDescriptionRequiredError}
                    helperText={
                      showDescriptionRequiredError ? 'Required' : undefined
                    }
                  />
                ) : (
                  <Box
                    sx={{
                      borderLeft: 3,
                      borderColor: 'primary.main',
                      backgroundColor: (t) =>
                        alpha(t.palette.primary.main, 0.06),
                      borderRadius: '0 4px 4px 0',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.925rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                      }}
                      color={
                        descriptionHasContent
                          ? 'text.primary'
                          : 'text.secondary'
                      }
                    >
                      {descriptionHasContent
                        ? voucher.description
                        : 'No description provided.'}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {/* Approval Stages */}
              <ApprovalStages voucher={voucher} />

              {isAwaitingApproval &&
                (voucherBudgetImpactResult.data?.voucherBudgetImpact?.length ??
                  0) > 0 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1.5 }}
                    >
                      Budget
                    </Typography>
                    <Table
                      size="small"
                      sx={{ '& td, & th': { fontSize: '0.9rem', py: 1.25 } }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            '& th': {
                              bgcolor: 'grey.100',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: 'text.secondary',
                            },
                          }}
                        >
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Budgeted</TableCell>
                          <TableCell align="right">Already Committed</TableCell>
                          <TableCell align="right">This Voucher</TableCell>
                          <TableCell align="right">If Approved</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {voucherBudgetImpactResult.data.voucherBudgetImpact.map(
                          (line: any) => (
                            <TableRow
                              key={
                                line.sub_cost_category?.id ??
                                line.cost_category.id
                              }
                            >
                              <TableCell>
                                {line.sub_cost_category?.name ??
                                  line.cost_category.name}
                              </TableCell>
                              <TableCell align="right">
                                {line.budgeted === null
                                  ? '—'
                                  : formatSek(line.budgeted)}
                              </TableCell>
                              <TableCell align="right">
                                {formatSek(line.already_committed)}
                              </TableCell>
                              <TableCell align="right">
                                {formatSek(line.this_voucher_amount)}
                              </TableCell>
                              <TableCell align="right">
                                {formatSek(line.would_be_total)}
                              </TableCell>
                              <TableCell align="center">
                                {line.budgeted === null ? (
                                  <Chip
                                    size="small"
                                    label="No budget set"
                                    sx={{ fontSize: '0.78rem' }}
                                  />
                                ) : line.exceeds_budget ? (
                                  <Chip
                                    size="small"
                                    color="error"
                                    label={`Over by ${formatSek(line.amount_over)}`}
                                    sx={{ fontSize: '0.78rem' }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    color="success"
                                    label="OK"
                                    sx={{ fontSize: '0.78rem' }}
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                )}

              <CostCategories
                voucher={voucher}
                readonly={!allowedToEdit}
                onAdd={handleAddCostCategory}
                onUpdate={handleUpdateCostCategory}
                onDelete={handleDeleteCostCategory}
                onOpenHelp={() => setHelpOpen(true)}
              />

              {voucher.state !== VoucherState.DRAFT && (
                <VoucherComments voucher={voucher} />
              )}
            </Box>
          </Grid>

          {/* Sidebar Metadata Column (25%) */}
          <Grid size={{ xs: 12, md: 3 }} order={{ xs: 1, md: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                backgroundColor: 'rgba(0,0,0,0.01)',
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                Voucher Details
              </Typography>
              <Box>
                <Box
                  sx={{
                    py: 1.1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    Created
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      fontFamily: 'monospace',
                    }}
                  >
                    {formatDate(voucher.created_at)}
                  </Typography>
                </Box>

                {voucher.submitted_at && (
                  <Box
                    sx={{
                      py: 1.1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                      }}
                    >
                      Submitted
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        fontFamily: 'monospace',
                      }}
                    >
                      {formatDate(voucher.submitted_at)}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    py: 1.1,
                    mb: 1.4,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    Created by
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 500 }}>
                    {voucher.created_by?.first_name}{' '}
                    {voucher.created_by?.last_name}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box>
                  <FieldLabel>Department</FieldLabel>
                  <FormControl component="div" fullWidth size="small">
                    <Select
                      id="department-select"
                      value={selectedDepartment || ''}
                      displayEmpty
                      onChange={(e: any) => {
                        setSelectedDepartment(e.target.value)
                        setVoucher({
                          ...voucher,
                          department: departments.find(
                            (d) => d.id === e.target.value,
                          ),
                        })
                      }}
                      disabled={!canChangeDepartment}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                      sx={selectSx}
                    >
                      {getAvailableDepartments(
                        departments,
                        authState.authUser,
                        voucher?.branch,
                      ).map((d: any) => (
                        <MenuItem
                          key={d.id}
                          value={d.id}
                          sx={{ fontSize: '0.95rem' }}
                        >
                          {d.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <FieldLabel>Financial year</FieldLabel>
                  <FormControl component="div" fullWidth size="small">
                    <Select
                      id="financial-year-select"
                      value={selectedFinancialYear || ''}
                      displayEmpty
                      onChange={(e: any) => {
                        setSelectedFinancialYear(e.target.value)
                        setVoucher({
                          ...voucher,
                          financial_year: financialYears.find(
                            (fy) => fy.id === e.target.value,
                          ),
                        })
                      }}
                      disabled={!isNationalMaal}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                      sx={selectSx}
                    >
                      {financialYears.map((fy: any) => (
                        <MenuItem
                          key={fy.id}
                          value={fy.id}
                          sx={{ fontSize: '0.95rem' }}
                        >
                          {fy.year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <FieldLabel>Pay to (type)</FieldLabel>
                  <FormControl
                    fullWidth
                    size="small"
                    error={showValidationError && !payToType}
                  >
                    <Select
                      value={payToType}
                      displayEmpty
                      onChange={(e: any) => setPayToType(e.target.value)}
                      disabled={
                        !allowedToEdit || voucher.state === VoucherState.PAID
                      }
                      sx={selectSx}
                    >
                      <MenuItem value="AMLA" sx={{ fontSize: '0.95rem' }}>
                        Amla
                      </MenuItem>
                      <MenuItem value="NON_AMLA" sx={{ fontSize: '0.95rem' }}>
                        Other
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {payToType && (
                  <Box>
                    <FieldLabel>Recipient</FieldLabel>
                    {payToType === 'AMLA' ? (
                      <Autocomplete
                        options={users
                          .filter((u: User) => {
                            if (authState.authUser.branch === 'NATIONAL') {
                              return (
                                u.branch === 'NATIONAL' ||
                                u.department?.name?.toLowerCase() ===
                                  'missionary'
                              )
                            }
                            return u.branch === authState.authUser.branch
                          })
                          .sort((a, b) =>
                            a.first_name.localeCompare(b.first_name),
                          )}
                        getOptionLabel={(u) => `${u.first_name} ${u.last_name}`}
                        value={
                          users.find(
                            (u) =>
                              `${u.first_name} ${u.last_name}` ===
                              voucher.pay_to,
                          ) || null
                        }
                        onChange={(event: any, newValue: any | null) => {
                          setVoucher({
                            ...voucher,
                            pay_to: newValue
                              ? `${newValue.first_name} ${newValue.last_name}`
                              : '',
                          })
                        }}
                        disabled={
                          !allowedToEdit || voucher.state === VoucherState.PAID
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder="Search by name"
                            disabled={
                              !allowedToEdit ||
                              voucher.state === VoucherState.PAID
                            }
                            error={
                              showValidationError &&
                              (!voucher.pay_to || voucher.pay_to.trim() === '')
                            }
                            InputProps={{
                              ...params.InputProps,
                              style: { fontSize: '0.95rem' },
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Recipient name"
                        value={voucher.pay_to || ''}
                        variant="outlined"
                        onChange={(e: any) =>
                          setVoucher({ ...voucher, pay_to: e.target.value })
                        }
                        disabled={
                          !allowedToEdit || voucher.state === VoucherState.PAID
                        }
                        error={
                          showValidationError &&
                          (!voucher.pay_to || voucher.pay_to.trim() === '')
                        }
                        InputProps={{ style: { fontSize: '0.95rem' } }}
                      />
                    )}
                  </Box>
                )}

                {/* Invoice currency and paying account, see
                    docs/requirements/payment-account-and-currency.md */}
                {voucher?.branch === Branch.NATIONAL && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <FieldLabel>Invoice currency</FieldLabel>
                      <FormControl fullWidth size="small">
                        <Select
                          id="invoice-currency-select"
                          value={voucher.invoice_currency || 'SEK'}
                          sx={selectSx}
                          onChange={(e) =>
                            handleInvoiceCurrencyChange(e.target.value)
                          }
                          disabled={!canEditCurrency}
                        >
                          {[
                            'CNH',
                            'DKK',
                            'EUR',
                            'GBP',
                            'NOK',
                            'SEK',
                            'USD',
                          ].map((curr) => (
                            <MenuItem
                              key={curr}
                              value={curr}
                              sx={{ fontSize: '0.95rem' }}
                            >
                              {curr}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {showAmountCurrencyOptions && (
                      <FormControl
                        error={showValidationError && !voucher.currency}
                        disabled={!canEditCurrency}
                      >
                        <RadioGroup
                          value={voucher.currency || ''}
                          onChange={(e) =>
                            handleAmountCurrencyChange(e.target.value)
                          }
                        >
                          <FormControlLabel
                            value="SEK"
                            control={<Radio size="small" />}
                            label={
                              <Typography sx={{ fontSize: '0.9rem' }}>
                                {isPaymentStage
                                  ? 'Amount is in SEK, converted after payment'
                                  : 'Already paid by me, claim in SEK'}
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value={voucher.invoice_currency}
                            control={<Radio size="small" />}
                            label={
                              <Typography sx={{ fontSize: '0.9rem' }}>
                                {isPaymentStage
                                  ? `Amount is still in ${voucher.invoice_currency}`
                                  : `Pay the supplier in ${voucher.invoice_currency}`}
                              </Typography>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                    {isNationalMaal ? (
                      <Box>
                        <FieldLabel>Paid from</FieldLabel>
                        <FormControl
                          fullWidth
                          size="small"
                          error={isForeignAmount && !voucher.payment_account}
                        >
                          <Select
                            id="payment-account-select"
                            value={voucher.payment_account ?? ''}
                            displayEmpty
                            sx={selectSx}
                            onChange={(e) =>
                              setVoucher((prev) => ({
                                ...prev,
                                payment_account: e.target
                                  .value as PaymentAccount,
                              }))
                            }
                            disabled={!allowedToEdit}
                          >
                            {(
                              Object.keys(
                                PAYMENT_ACCOUNT_LABELS,
                              ) as PaymentAccount[]
                            )
                              // Amin's account can only pay in SEK.
                              .filter(
                                (account) =>
                                  !isForeignAmount || account !== 'AMIN',
                              )
                              .map((account) => (
                                <MenuItem
                                  key={account}
                                  value={account}
                                  sx={{ fontSize: '0.95rem' }}
                                >
                                  {PAYMENT_ACCOUNT_LABELS[account]}
                                </MenuItem>
                              ))}
                          </Select>
                          {isForeignAmount && !voucher.payment_account && (
                            <FormHelperText>
                              Choose an account before approving.
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    ) : (
                      voucher.payment_account && (
                        <Typography variant="caption" color="text.secondary">
                          Paid from{' '}
                          {PAYMENT_ACCOUNT_LABELS[voucher.payment_account]}
                        </Typography>
                      )
                    )}
                  </Box>
                )}

                <Box>
                  <FieldLabel>Expense month</FieldLabel>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={voucher.expense_period || ''}
                      displayEmpty
                      disabled={!isNationalMaal}
                      onChange={(e) =>
                        setVoucher({
                          ...voucher,
                          expense_period: parseInt(e.target.value as string),
                        })
                      }
                      sx={selectSx}
                    >
                      {voucher.expense_period &&
                        getExpenseMonthOptions(voucher.expense_period).map(
                          (opt) => (
                            <MenuItem
                              key={opt.key}
                              value={opt.key}
                              sx={{ fontSize: '0.95rem' }}
                            >
                              {opt.value}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Paper>

            {/* Approval Actions Section in Sidebar */}
            {voucher &&
              voucher.state !== VoucherState.PAID &&
              isCurrentApprover && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 2,
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    borderTop: 4,
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                    }}
                  >
                    Actions
                  </Typography>
                  {isApprovalDisabled && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Voucher total is 0. Please add cost categories to enable
                      approval actions.
                    </Alert>
                  )}
                  {isPaymentBlockedByCurrency && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Enter the SEK amount drawn on the cost lines and switch
                      the amount to SEK before marking as paid.
                    </Alert>
                  )}
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {isMarkingAsPaid ? (
                      <Box display="flex" flexDirection="column" gap={2}>
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={sv}
                        >
                          <DatePicker
                            label="Payment Date"
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true,
                              },
                            }}
                            value={
                              voucher.paid_by_amin
                                ? new Date(
                                    typeof voucher.paid_by_amin === 'string' &&
                                      /^\d+$/.test(voucher.paid_by_amin)
                                      ? Number(voucher.paid_by_amin)
                                      : voucher.paid_by_amin,
                                  )
                                : new Date()
                            }
                            onChange={(newValue: Date | null) => {
                              if (newValue) {
                                setVoucher({
                                  ...voucher,
                                  paid_by_amin: newValue.toISOString(),
                                })
                              }
                            }}
                          />
                        </LocalizationProvider>

                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          label="Payment reference"
                          value={voucher.payment_reference || ''}
                          variant="outlined"
                          onChange={(e) =>
                            setVoucher({
                              ...voucher,
                              payment_reference: e.target.value,
                            })
                          }
                        />
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        label="Reason / Comments"
                        value={comment}
                        variant="outlined"
                        onChange={(e) => setComment(e.target.value)}
                        disabled={isApprovalDisabled}
                        InputProps={{ style: { fontSize: '0.95rem' } }}
                      />
                    )}
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap={1}
                      sx={{ mt: 1 }}
                    >
                      {!isMarkingAsPaid && (
                        <Button
                          variant="outlined"
                          color="error"
                          sx={{
                            flex: 1,
                            height: 42,
                            fontSize: '0.92rem',
                            fontWeight: 700,
                          }}
                          onClick={() => setRejectDialogOpen(true)}
                          disabled={isApprovalDisabled}
                        >
                          Reject
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{
                          flex: 1,
                          height: 42,
                          fontSize: '0.92rem',
                          fontWeight: 700,
                        }}
                        onClick={() => {
                          if (isVoucherDirty) {
                            setPendingAction('APPROVE')
                            setUnsavedChangesDialogOpen(true)
                          } else {
                            openApproveConfirm()
                          }
                        }}
                        disabled={
                          isApprovalDisabled || isPaymentBlockedByCurrency
                        }
                      >
                        {isMarkingAsPaid ? 'Mark as Paid' : 'Approve'}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              )}

            {voucher.id && (
              <VoucherTags
                voucherId={voucher.id as string}
                initialTags={voucher.tags}
              />
            )}
          </Grid>
        </Grid>

        {showBottomBar && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              // Clear of the permanent sidebar, which otherwise covers the bar.
              left: { xs: 0, md: DRAWER_WIDTH },
              right: 0,
              zIndex: 1100,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
              py: 1.5,
            }}
          >
            <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
              {showValidationError && validationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {validationError}
                </Alert>
              )}

              <Box display="flex" justifyContent="flex-end" gap={2}>
                {voucher.state === 'DRAFT' && voucher.workflow && (
                  <>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleSubmit(VoucherState.DRAFT)}
                    >
                      Save Draft
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (
                          voucher.state === VoucherState.DRAFT &&
                          validationError
                        ) {
                          setShowValidationError(true)
                          return
                        }
                        setShowValidationError(false)
                        setSubmissionConfirmOpen(true)
                      }}
                    >
                      Submit
                    </Button>
                  </>
                )}
                {(allowedToEdit || canChangeDepartment) &&
                  voucher.state !== VoucherState.DRAFT &&
                  voucher.state !== VoucherState.REJECTED && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpdate}
                    >
                      Update
                    </Button>
                  )}
              </Box>
            </Container>
          </Box>
        )}

        <CustomDialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title="Category List"
        >
          <CategoryHelpList />
        </CustomDialog>

        <VhsDialog
          open={approveConfirmOpen}
          onClose={() => {
            setApproveConfirmOpen(false)
            setBudgetWarning(null)
          }}
          onConfirm={() => {
            setApproveConfirmOpen(false)
            setBudgetWarning(null)
            handleApprove()
          }}
          title={isMarkingAsPaid ? 'Mark Voucher as Paid?' : 'Approve Voucher?'}
          warningMessage={
            isMarkingAsPaid
              ? `Mark as paid: ${formatSek(voucher.total_amount)}${
                  voucher.payment_account
                    ? ` from ${PAYMENT_ACCOUNT_LABELS[voucher.payment_account]}`
                    : ''
                }. Please check this is the amount actually drawn.`
              : budgetWarning
                ? `Are you sure you want to approve this voucher? ${budgetWarning}`
                : 'Are you sure you want to approve this voucher?'
          }
          color={budgetWarning ? 'warning' : 'primary'}
          confirmButtonText={isMarkingAsPaid ? 'Mark as Paid' : 'Approve'}
          showReasonField={false}
        />

        <VhsDialog
          open={rejectDialogOpen}
          onClose={() => setRejectDialogOpen(false)}
          onConfirm={(reason) => {
            setComment(reason)
            handleConfirmReject(reason)
          }}
          initialReason={comment}
          title="Reject Voucher?"
          warningMessage="Warning: This operation is irreversible and the voucher flow will end here."
          reasonLabel="Rejection Reason"
          confirmButtonText="Reject Voucher"
          color="error"
          isReasonMandatory={true}
        />

        <VhsDialog
          open={submissionConfirmOpen}
          onClose={() => setSubmissionConfirmOpen(false)}
          onConfirm={() => {
            setSubmissionConfirmOpen(false)
            handleSubmit(VoucherState.SUBMITTED)
          }}
          title={
            hasAnyReceipts ? 'Submit Voucher?' : 'Submit without Receipts?'
          }
          warningMessage={
            hasAnyReceipts
              ? 'Are you sure you want to submit this voucher for approval?'
              : 'No receipts have been attached to any of the cost categories. Do you want to submit anyway?'
          }
          confirmButtonText={hasAnyReceipts ? 'Submit' : 'Submit Anyway'}
          showReasonField={false}
        />

        <VhsDialog
          open={unsavedChangesDialogOpen}
          onClose={() => {
            setUnsavedChangesDialogOpen(false)
            setPendingAction(null)
          }}
          onConfirm={handleConfirmUnsaved}
          onSecondaryConfirm={handleDiscardUnsaved}
          title="Unsaved Changes"
          warningMessage="You have unsaved changes. Do you want to save them before proceeding?"
          confirmButtonText="Save and Proceed"
          secondaryConfirmButtonText="Discard and Proceed"
          showReasonField={false}
        />
      </Container>
    </Paper>
  )
}

export default Details
